<template>
  <v-row>
    <v-col cols="12">
      <v-table-toolbar>
        <v-spacer/>
        <toolbar-menu
          :label="$t('buttons.columns')"
          class="mr-2"
          icon="far fa-columns"
        >
          <v-list-item :key="column.modelKey" v-for="column in columns"
          >
            <v-checkbox-btn
              :label="column.title"
              v-model="column.visible"
              :color="column.visible ? 'primary' : 'grey'"
              :data-pw="`${column.modelKey}-column`"
            />
          </v-list-item>
        </toolbar-menu>
        <data-export-download-menu v-if="isAdminOrRoot" :formats="['csv', 'excel']" @click="handleExportClick"/>
      </v-table-toolbar>

    </v-col>
  </v-row>
  <v-row>
    <v-col cols="2">
      <table-filter-card @resetFilters="handleReset" :active-filter-count="activeFilterCount">
        <table-filter-card-item title="Search">
          <search-text-field
            :label="$t('common.search_user_details')"
            data-pw="emailSearchBox"
            :disabled="loading"
            :search-string="searchString"
            @update:text="text => {
              handleSearch(text)
            }"
          />
        </table-filter-card-item>
        <table-filter-card-item title="Email">
          <filter-text-field
            :label="columns.email.title"
            data-pw="userEmailFilterTextBox"
            :disabled="store.networkBusy"
            v-model="emailFilter"
            @change="columns.email.filterText = `${ columns.email.filter}`"
          />
        </table-filter-card-item>
        <table-filter-card-item title="Phone">
          <filter-text-field
            data-pw="userPhoneFilterTextBox"
            :label="columns.phone.title"
            :disabled="store.networkBusy"
            v-model="phoneFilter"
            @change="columns.phone.filterText = `${ columns.phone.filter}`"
          />
        </table-filter-card-item>
        <table-filter-card-item v-if="showTenant" title="Tenant">
          <tenant-search-autocomplete
            v-if="initialised"
            v-model="selectedTenant"
            entity-type="tenant"
          />
        </table-filter-card-item>
        <table-filter-card-item v-if="showInstitution" title="Institution">
          <tenant-search-autocomplete
            v-if="initialised"
            v-model="selectedInstitution"
            entity-type="institution"
            :tenant-ids="selectedTenant ? [selectedTenant] : []"
          />
        </table-filter-card-item>
        <table-filter-card-item v-if="!hiddenFields.includes('cohort')" title="Cohort">
          <tenant-search-autocomplete
            v-if="initialised"
            v-model="selectedCohort"
            entity-type="cohort"
            :tenant-ids="selectedTenant ? [selectedTenant] : []"
            :institution-ids="selectedInstitution ? [selectedInstitution] : []"
          />
        </table-filter-card-item>
        <table-filter-card-item title="First Name" :open-by-default="false">
          <filter-text-field
            :label="$t('components.UserTablePage.firstName')"
            v-model="firstNameFilter"
            @change="columns.firstName.filterText = `${columns.firstName.filter}`"
          />
        </table-filter-card-item>
        <table-filter-card-item title="Last Name" :open-by-default="false">
          <filter-text-field
            :label="$t('components.UserTablePage.lastName')"
            v-model="lastNameFilter"
            @change="columns.lastName.filterText = `${columns.lastName.filter}`"
          />
        </table-filter-card-item>
        <table-filter-card-item title="Role" :open-by-default="false">
          <select-role
            :model-value="columns.role.filter as Role"
            type="filter"
            @update:model-value="updateRoleFilter"
            data-pw="userRoleFilter"
          />
        </table-filter-card-item>

        <table-filter-card-item title="Account Type" :open-by-default="false">
          <select-account-type
            :label="columns.accountType.title"
            data-pw="accountTypeFilter"
            v-model="columns.accountType.filter"
            @update:modelValue="columns.accountType.filterText = $t(`accountTypes.${columns.accountType.filter}`)"
          />
        </table-filter-card-item>
        <table-filter-card-item title="Terms Consent" :open-by-default="false">
          <v-radio-group
            v-model="columns.termsConsent.filter"
            data-pw="userTermsConsentFilter"
            :disabled="store.networkBusy"
            density="compact"
          >
            <v-radio
              :label="$t('common.yes')"
              value="true"
              data-pw="userTermsConsentFilterYes"
              @change="columns.termsConsent.filterText = 'Yes'"
            />
            <v-radio
              :label="$t('common.no')"
              value="false"
              data-pw="userTermsConsentFilterNo"
              @change="columns.termsConsent.filterText = 'No'"
            />
            <v-radio
              :label="$t('common.all')"
              value="all"
              data-pw="userTermsConsentFilterAll"
              @change="columns.termsConsent.filterText = 'All'"
            />
          </v-radio-group>
        </table-filter-card-item>
        <table-filter-card-item title="Created At" :open-by-default="false">
          <vue-date-picker
            v-model="createdAtDateFilter"
            range
            auto-apply
            multi-calendars
            :enable-time-picker="false"
          />
        </table-filter-card-item>
      </table-filter-card>
    </v-col>
    <v-col md="9" lg="10">
      <v-data-table-server
        v-model="selectedItems"
        class="mb-6"
        color="bg-transparent"
        :loading="loadingData"
        :headers="headers"
        :items="users"
        :items-per-page="perPage"
        :items-length="0"
        @click:row="onClickRow"
        @update:sort-by="onSort"
        :sort-by="sort"
        :hover="true"
        data-pw="usersTable"
        show-select
        return-object
        hide-default-footer
      >
        <template v-slot:no-data>
          <h5 class="pa-2 d-flex align-center justify-center align-items-center">{{ $t("common.no_data_message") }}</h5>
        </template>
        <template v-slot:loader>
          <v-sheet
            class="d-flex align-center justify-center"
            height="100%"
          >
            <v-progress-linear
              indeterminate
              color="primary"
            ></v-progress-linear>
          </v-sheet>
        </template>

        <template v-slot:top>
          <!-- Bulk Actions Toolbar -->
          <v-toolbar
            v-if="selectedItems && selectedItems.length > 0"
            class="bulk-actions-toolbar"
            color="primary"
            dark
            dense
            flat
          >
            <v-toolbar-title class="text-body-2">
              {{ selectedItems.length }} {{ selectedItems.length === 1 ? 'user' : 'users' }} selected
            </v-toolbar-title>

            <v-spacer />

            <v-btn
              variant="text"
              size="small"
              @click="selectedItems = []"
            >
              Clear Selection
            </v-btn>

            <v-divider vertical class="mx-2" />

            <!-- Bulk action buttons will go here -->
            <v-btn
              v-if="isAdminOrRoot"
              variant="text"
              size="small"
              @click="showMoveDialog = true"
            >
              <v-icon start>fas fa-users</v-icon>
              Move
            </v-btn>

          </v-toolbar>
        </template>

        <template v-slot:item.app.accountType="{ item }">
          <v-account-type-chip :account-type="item.app.accountType"/>
        </template>

        <template v-slot:item.app.role="{ item }">
          {{ $t(`roles.${item.app.role}`) }}
        </template>

        <template v-slot:item.consents.terms.granted="{ item }">
          <v-icon v-if="item.consents?.terms?.granted">
            far fa-check
          </v-icon>
        </template>

        <template v-slot:item.consents.terms.updatedAt="{ item }">
          {{ toLocaleDateTime(item.consents?.terms?.updatedAt?.toString()) }}
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ toLocaleDateTime(item["createdAt"]?.toString()) }}
        </template>

        <template v-slot:bottom>
          <v-divider/>
          <pagination-controls
            v-if="paginatedData"
            class="text-center pt-2 v-table__pagination-controls"
            :pagination-footer-data="paginationFooterData"
            @update:per-page="onUpdatePerPage"
            @on-next="onNext"
            @on-prev="onPrev"
            @on-page-change="onPageChange"
            data-pw="userPaginationControls"
          />
        </template>

      </v-data-table-server>

    </v-col>
  </v-row>

  <v-dialog
    v-model="showExportDialog"
    max-width="500"
  >
    <export-user-dialog
      @close="showExportDialog = false"
      @download="exportPages"
      :format="exportFormat"
      :cols="cols"
    >
    </export-user-dialog>
  </v-dialog>

  <v-dialog
    v-model="showMoveDialog"
    max-width="500"
  >
    <move-users-form :users="selectedItems" @close="showMoveDialog = false" @success="handleCloseMove" />
  </v-dialog>

  <v-snackbar
    v-model="showExportingSnackBar">
    {{ $t("common.download_status") }}
    <v-progress-linear :indeterminate="true"/>
  </v-snackbar>
</template>

<script lang="ts" setup>
import {computed, onMounted, PropType, ref, Ref, watch} from "vue";
import {useRouter, useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import {
  AccountType,
  isYapiError,
  Paginated,
  DashUser,
  UserExportSelectors,
  UserPaginationParams,
  YapiError, Role, Institution, Tenant, Cohort,
} from "@YenzaCT/sdk";

import yapi from "@/lib/yapi";
import {useGlobalStore} from "@/store";

import PaginationControls from "@/components/PaginationControls.vue";
import {ColumnsConfig, FooterData, footerDataFactory, usePagination} from "@/lib/pagination";
import SearchTextField from "@/components/SearchTextField.vue";
import ToolbarMenu from "@/components/DataTable/ToolbarMenu.vue";
import FilterTextField from "@/components/DataTable/FilterTextField.vue";
import SelectAccountType from "@/components/SelectAccountType.vue";
import ExportUserDialog from "@/pages/Users/UserTablePage/components/ExportUserDialog.vue";
import {WritableComputedRef} from "@vue/reactivity";
import {toLocaleDateTime} from "@/lib/time";
import VueDatePicker from "@vuepic/vue-datepicker";
import {DateTime} from "luxon";
import {downloadCSV, downloadExcel} from "@/lib/fileDownload";
import DataExportDownloadMenu from "@/components/DataExportDownloadMenu.vue";
import SelectRole from "@/components/SelectRole.vue";
import VAccountTypeChip from "@/components/VAccountTypeChip.vue";
import TableFilterCardItem from "@/components/TableFilterCardItem.vue";
import TableFilterCard from "@/components/TableFilterCard.vue";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";
import MoveUsersForm from "@/pages/Users/UserTablePage/components/MoveUsersForm.vue";

const router = useRouter();
const route = useRoute();
const store = useGlobalStore();

const {t} = useI18n();
const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);
const termsConsentedAtDateFilter = ref<Date[]>([]);
const showExportDialog = ref(false);
const loadingData = ref(false);
const showMoveDialog = ref(false);
const exportFormat = ref("csv");
const loading = ref(false);
const initialised = ref(false);
const users: Ref<DashUser[]> = ref([]);
const paginatedData: Ref<Paginated<DashUser> | null> = ref(null);
let paginationFooterData: FooterData = footerDataFactory();
const showExportingSnackBar = ref<boolean>(false);
const selectedItems = ref<DashUser[]>();

function handleCloseMove() {
  showMoveDialog.value = false;
  selectedItems.value = [];
  fetchPage();
}

const searchString = ref(route.query.search as string);

const props = defineProps({
  institution: {
    type: Object as PropType<Institution>,
    required: false
  },
  tenant: {
    type: Object as PropType<Tenant>,
    required: false
  },
  cohort: {
    type: Object as PropType<Cohort>,
    required: false
  },
  hiddenFields: {
    type: Array as PropType<string[]>,
    default: (): string[] => []
  },
  clickRoute: {
    type: String as PropType<string>,
    default: "userDetail"
  },
  routeParams: {
    type: Object as PropType<Record<string, unknown>>,
    default: () => ({})
  }
});

const updateRoleFilter = (value: Role) => {
  columns.role.filter = value;
  columns.role.filterText = value ? t(`roles.${value}`) : "";
};

const showTenant = computed(() => {
  if (props.hiddenFields.includes("tenant"))
    return false;

  return true;
});

const showInstitution = computed(() => {
  if (props.hiddenFields.includes("institution"))
    return false;

  return isAdminOrRoot.value || store.accessibleTenants.length !== 0 || store.accessibleInstitutions.length !== 0;
});

const cols: ColumnsConfig = {
  id: {
    title: t("components.UserTablePage.table_headers.id"),
    modelKey: "_id",
    visible: false,
    filter: "",
    filterText: ""
  },
  tenant: {
    title: t("components.UserTablePage.table_headers.tenant"),
    modelKey: "tenantMeta.title",
    visible: !props.hiddenFields.includes("tenant"),
    filter: "",
    filterText: ""
  },
  institution: {
    title: t("components.UserTablePage.table_headers.institution"),
    modelKey: "institutionMeta.title",
    visible: !props.hiddenFields.includes("institution"),
    filter: "",
    filterText: ""
  },
  cohort: {
    title: t("components.UserTablePage.table_headers.cohort"),
    modelKey: "cohortMeta.title",
    visible: !props.hiddenFields.includes("cohort"),
    filter: "",
    filterText: ""
  },
  email: {
    title: t("components.UserTablePage.table_headers.email"),
    modelKey: "email",
    visible: true,
    filter: "",
    filterText: ""
  },
  phone: {
    title: t("components.UserTablePage.table_headers.phone"),
    modelKey: "phone",
    visible: true,
    filter: "",
    filterText: ""
  },
  firstName: {
    title: t("components.UserTablePage.table_headers.first_name"),
    modelKey: "profile.firstName",
    visible: true,
    filter: "",
    filterText: ""
  },
  lastName: {
    title: t("components.UserTablePage.table_headers.last_name"),
    modelKey: "profile.lastName",
    visible: true,
    filter: "",
    filterText: ""
  },
  accountType: {
    title: t("components.UserTablePage.table_headers.account_type"),
    modelKey: "app.accountType",
    visible: true,
    filter: "",
    filterText: ""
  },
  role: {
    title: t("components.UserTablePage.table_headers.role"),
    modelKey: "app.role",
    visible: false,
    filter: "",
    filterText: ""
  },
  termsConsent: {
    title: t("components.UserTablePage.table_headers.terms_consent"),
    modelKey: "consents.terms.granted",
    visible: false,
    filter: "",
    filterText: ""
  },
  termsConsentedAt: {
    title: t("components.UserTablePage.table_headers.terms_consented_at"),
    modelKey: "consents.terms.updatedAt",
    visible: false,
    filter: [],
    filterText: ""
  },
  createdAt: {
    title: t("components.UserTablePage.table_headers.created_at"),
    modelKey: "createdAt",
    visible: true,
    filter: [],
    filterText: ""
  },
};

const {
  columns,
  headers,
  page,
  perPage,
  sort,
  activeFilterCount,
  createdAtDateFilter,
  selectedTenant,
  selectedInstitution,
  selectedCohort,
  watchDateFilter,
  resetFilter,
  createUrlFilterParameters,
  onUpdatePerPage,
  onSort,
  onNext,
  onPrev,
  onPageChange,
  setupFooterData,
  setupBasicParams
} = usePagination(fetchPage, {
  initialItemsPerPage: 20,
  initialColumns: cols,
  initialSort: [{
    key: "createdAt",
    order: "desc"
  }]
});

onMounted(async () => {
  loadingData.value = true;

  if (searchString.value) {
    handleSearch(searchString.value);
    return;
  }

  if (props.tenant && props.tenant._id) {
    columns.tenant.filter = props.tenant._id;
    // Update the store so the dropdown reflects the preloaded value
    selectedTenant.value = props.tenant._id;
  } else {
    columns.tenant.filter = route.query.filterTenant as string || columns.tenant.filter;
    selectedTenant.value = route.query.filterTenant as string || null;
  }

  if (props.institution && props.institution._id) {
    columns.institution.filter = props.institution._id;
    // Update the store so the dropdown reflects the preloaded value
    selectedInstitution.value = props.institution._id;
  } else {
    columns.institution.filter = route.query.filterInstitution as string || columns.institution.filter;
    selectedInstitution.value = route.query.filterInstitution as string || null;
  }

  if (props.cohort && props.cohort._id) {
    columns.cohort.filter = props.cohort._id;
    // Update the store so the dropdown reflects the preloaded value
    selectedCohort.value = props.cohort._id as string;
  } else {
    columns.cohort.filter = route.query.filterCohort as string || columns.cohort.filter;
    selectedCohort.value = route.query.filterCohort as string || null;
  }

  setupPageParams();

  initialised.value = true;

  await fetchPage();

  loadingData.value = false;
});

const emailFilter: WritableComputedRef<string> = computed({
  get: (): string => columns.email.filter.toString(),
  set: (val: string | string[]) => {
    columns.email.filter = val.toString();
  }
});

const phoneFilter: WritableComputedRef<string> = computed({
  get: (): string => columns.phone.filter.toString(),
  set: (val: string | string[]) => {
    columns.phone.filter = val.toString();
  }
});

const firstNameFilter: WritableComputedRef<string> = computed({
  get: (): string => columns.firstName.filter.toString(),
  set: (val: string | string[]) => {
    columns.firstName.filter = val.toString();
  }
});

const lastNameFilter: WritableComputedRef<string> = computed({
  get: (): string => columns.lastName.filter.toString(),
  set: (val: string | string[]) => {
    columns.lastName.filter = val.toString();
  }
});

/**
 * Watch for changes to the search query param when using global search
 */
watch(() => route.query.search, async (newSearchValue) => {
  if (newSearchValue && newSearchValue !== searchString.value) {
    resetFilter(props.hiddenFields);
    handleSearch(newSearchValue as string);
  }
});

watchDateFilter(termsConsentedAtDateFilter, columns.termsConsentedAt);
watchDateFilter(createdAtDateFilter, columns.createdAt);

function handleReset() {
  searchString.value = "";
  resetFilter(props.hiddenFields);
}

/**
 * Export a CSV or Excel of documents based on the current filters
 * @param selected The selected fields to export
 */
async function exportPages(selected: UserExportSelectors) {
  // Close the dialog
  showExportDialog.value = false;
  try {
    store.networkBusy = true;
    loading.value = true;
    showExportingSnackBar.value = true;

    const params = {
      ...filterParams.value,
      format: "csv"
    };

    if (exportFormat.value === "excel")
      params.format = "excel";

    const res = await yapi.admin.user.export({selectors: selected}, params);

    const now = new Date();
    const timestamp = now.toLocaleString();
    if (exportFormat.value === "excel") {
      downloadExcel(res.data.content.toString(), `user-${timestamp}`);
    } else {
      downloadCSV(res.data.content.toString(), `user-${timestamp}`);
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    showExportingSnackBar.value = false;
    loading.value = false;
    store.networkBusy = false;
  }
}

async function fetchPage() {
  loadingData.value = true;

  const params: UserPaginationParams = {
    page: page.value,
    size: perPage.value,
    sortBy: sort.value[0].key,
    sortOrder: sort.value[0].order,
    ...filterParams.value
  };

  try {
    store.networkBusy = true;
    paginatedData.value = (await yapi.admin.user.paginate(params)).data;
    await router.push({query: createUrlFilterParameters(params)});
    users.value = paginatedData.value.docs;
    const footerData = Object.assign(paginatedData.value);
    footerData.totalPages = paginatedData.value?.hasNextPage
      ? paginatedData.value.page + 1
      : paginatedData.value.page;
    paginationFooterData = setupFooterData(footerData);
    selectedItems.value = [];
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
    loadingData.value = false;
  }
}

const filterParams = computed(() => ({
  filterEmail: columns.email.filter as string || undefined,
  filterPhone: columns.phone.filter as string || undefined,
  filterFirstName: columns.firstName.filter as string || undefined,
  filterLastName: columns.lastName.filter as string || undefined,
  filterTermsConsent: columns.termsConsent.filter as string || undefined,
  filterAccountType: columns.accountType.filter as AccountType || undefined,
  filterTenant: columns.tenant.filter as string || undefined,
  filterInstitution: columns.institution.filter as string || undefined,
  filterCohort: columns.cohort.filter as string || undefined,
  filterRole: columns.role.filter as Role || undefined,
  filterCreatedAtStart: DateTime.fromISO(columns.createdAt.filter[0])
    .startOf("day")
    .toISO() as string || undefined,
  filterCreatedAtEnd: DateTime.fromISO(columns.createdAt.filter[1])
    .endOf("day")
    .toISO() as string || undefined,
  filterTermsConsentedAtStart: DateTime.fromISO(columns.termsConsentedAt.filter[0])
    .startOf("day")
    .toISO() as string || undefined,
  filterTermsConsentAtEnd: DateTime.fromISO(columns.termsConsentedAt.filter[0])
    .endOf("day")
    .toISO() as string || undefined
}));

function handleExportClick(format: string) {
  showExportDialog.value = true;
  exportFormat.value = format;
}

/**
 * Generate the params for pagination or export
 */
function setupPageParams() {
  setupBasicParams(page, perPage, sort, route);
  columns.email.filter = route.query.filterEmail as string || columns.email.filter;
  columns.phone.filter = route.query.filterPhone as string || columns.phone.filter;
  columns.firstName.filter = route.query.filterFirstName as string || columns.firstName.filter;
  columns.lastName.filter = route.query.filterLastName as string || columns.lastName.filter;
  columns.accountType.filter = route.query.filterAccountType as AccountType || columns.accountType.filter;
  columns.termsConsent.filter = route.query.filterTermsConsent as string || columns.termsConsent.filter;
  columns.tenant.filter = route.query.filterTenant as string || columns.tenant.filter;
  columns.institution.filter = route.query.filterInstitution as string || columns.institution.filter;
  columns.cohort.filter = route.query.filterCohort as string || columns.cohort.filter;
  columns.role.filter = route.query.filterRole as Role || columns.role.filter;
}

const onClickRow = async (event: Event, row: { internalItem: { raw?: DashUser } }) => {
  try {
    if (row.internalItem && row.internalItem.raw) {
      const user = row.internalItem.raw;
      if (user._id) {
        await navigateToUser(user);
      }
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  }
};

function handleSearch(searchText: string) {
  columns.email.filter = searchText;
  columns.email.filterText = searchText;
  columns.phone.filter = searchText;
  columns.phone.filterText = searchText;
  columns.firstName.filter = searchText;
  columns.firstName.filterText = searchText;
  columns.lastName.filter = searchText;
  columns.lastName.filterText = searchText;
  searchString.value = searchText;
}

const navigateToUser = async (user: DashUser) => {
  const routeName = props.clickRoute || "userDetail";

  let params: Record<string, unknown>;
  if (routeName === "tenantUserDetail") {
    params = {tenantId: user.app.tenant, userId: user._id};
  } else if (routeName === "institutionUserDetail") {
    params = {tenantId: user.app.tenant, institutionId: user.app.institution, userId: user._id};
  } else if (routeName === "cohortUserDetail") {
    params = {tenantId: user.app.tenant, institutionId: user.app.institution, cohortId: user.app.cohort, userId: user._id};
  } else {
    params = {...props.routeParams, id: user._id};
  }

  await router.push({
    name: routeName,
    params: {...params, userId: user._id}
  });
};

</script>

<style>
.bulk-actions-toolbar {
  margin-top: -1px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
